<template>
  <ModalCard
    v-if="isOpened"
    title="Copy datasets to S3"
    width="50%"
    :handle-close="() => closeModal()"
  >
    <template #body>
      <template v-if="isLoading">
        <section
          class="is-flex is-justify-content-center is-align-items-center"
          style="font-size: 22px; width: 100%; height: 100%"
        >
          <span class="icon is-large">
            <i class="fa fa-spinner fa-spin" />
          </span>
        </section>
      </template>
      <template v-else-if="datasetsCount === 0">
        <div class="notification is-danger">
          <span class="icon">
            <i class="fa fa-exclamation-triangle" />
          </span>
          <span class="text has-text-left">
            <p>No datasets found to copy.</p>
          </span>
        </div>
      </template>
      <template v-else>
        <template v-if="transferableDatasetsCount === 0">
          <div class="notification is-danger">
            <span class="text">Nothing to transfer</span>
          </div>
        </template>
        <DefaultPanel
          :is-collapsible="true"
          class="pb-0"
          style="overflow: unset"
        >
          <template #header>
            <div>
              {{ pluralize('Dataset', datasetsCount) }} Information
              <span
                class="tag is-rounded ml-2"
                :class="{
                  'is-success': transferableDatasetsCount === datasetsCount,
                  'is-warning': transferableDatasetsCount < datasetsCount,
                  'is-danger': transferableDatasetsCount === 0,
                }"
              >
                {{ transferableDatasetsCount }} / {{ datasetsCount }}
              </span>
            </div>
          </template>
          <div>
            <template
              v-for="d of [
                {
                  panelName: 'Non Transferable Datasets',
                  datasets: nonTransferableDatasets,
                  collapsed: false,
                  tagClass: 'is-danger',
                },
                {
                  panelName: 'Transferable Datasets',
                  datasets: transferableDatasets,
                  collapsed: true,
                  tagClass: 'is-success',
                },
              ]"
              :key="`panel-${d.panelName}`"
            >
              <DefaultPanel
                v-if="d.datasets.length > 0"
                :is-collapsible="true"
                :collapsed="d.collapsed"
                class="mb-0"
                :header-styles="{
                  'background-color': '#F6F8FB',
                  'border-radius': 0,
                  height: '2.5rem',
                }"
              >
                <template #header>
                  <div class="is-size-6">
                    <span class="tag is-rounded mr-4" :class="[d.tagClass]">
                      {{ d.datasets.length }}
                    </span>
                    {{ d.panelName }}
                  </div>
                </template>
                <DynamicScroller
                  class="scroller box"
                  :items="Object.values(d.datasets)"
                  :min-item-size="44"
                  :style="{
                    'border-radius': 0,
                    'min-height': `${Object.values(d.datasets) * 44}px`,
                  }"
                >
                  <template #default="{ item, index, active }">
                    <DynamicScrollerItem
                      :item="item"
                      :active="active"
                      :data-index="index"
                      class="dynam-item is-flex is-align-items-center p-2"
                      :class="{ even: index % 2 == 0 }"
                    >
                      <span class="icon">
                        <i
                          v-if="item.canCopy"
                          class="fa-solid fa-check-circle has-text-success"
                        />
                        <i
                          v-else
                          class="fa-solid fa-circle-xmark has-text-danger"
                          style="opacity: 0.7"
                        />
                      </span>
                      <span
                        class="label is-inline is-flex-grow-1 is-size-7 m-1"
                      >
                        {{ item.name }}
                      </span>
                      <span>
                        <StatusWidget
                          :tooltip-text="`Dataset is ${
                            item.isOnline ? 'online' : 'not online'
                          }`"
                          class="mr-1"
                          :status="item.status"
                          :status-color="item.isOnline ? 'success' : 'danger'"
                        />
                        <span
                          v-tooltip="
                            `Permissions are ${
                              item.hasPerms ? 'sufficient' : 'not sufficient'
                            } to copy the file`
                          "
                          class="icon hoverable-icon"
                        >
                          <i
                            v-if="item.hasPerms"
                            class="fa-solid fa-share-alt has-text-success"
                          />
                          <i v-else class="fa-solid fa-ban has-text-danger" />
                        </span>
                      </span>
                    </DynamicScrollerItem>
                  </template>
                </DynamicScroller>
              </DefaultPanel>
            </template>
          </div>
        </DefaultPanel>
        <DefaultPanel
          :is-collapsible="true"
          :collapsed="datasetsCount === 0 || transferableDatasetsCount === 0"
        >
          <template #header>
            <div class="is-size-6">Copy options</div>
          </template>
          <fieldset :disabled="transferableDatasetsCount === 0">
            <div class="field">
              <a
                style="cursor: pointer !important; text-decoration: underline"
                @click="showSourceInfo = !showSourceInfo"
              >
                <label
                  class="label is-size-7 mx-1 mb-1"
                  style="cursor: pointer !important"
                >
                  Source Volume
                  <span class="icon is-small">
                    <i
                      v-if="!showSourceInfo"
                      class="fa fa-chevron-down fa-xs"
                    />
                    <i v-else class="fa fa-chevron-up fa-xs" />
                  </span>
                </label>
              </a>
              <p
                v-if="showSourceInfo"
                class="notification is-info is-size-7 p-2"
              >
                <span class="icon">
                  <i class="fa fa-info" />
                </span>
                The source volume will be automatically decided for each
                dataset. Only volumes you have access to will be considered.
              </p>
            </div>
            <div class="field">
              <label class="label is-size-7 mx-1 mb-1">
                S3 Destination Volume
              </label>
              <SelectContainer
                v-model="selectedDestinVolume"
                class="control"
                :options="destinationVolumesOpts"
                :preselect-first="true"
                :placeholder="
                  destinationVolumesOpts.length > 0
                    ? 'Select a Storage Volume'
                    : 'No available destination volume'
                "
                :is-loading="isLoadingStorageVolumes"
                :disabled="
                  errors['storage-volumes'] ||
                  destinationVolumesOpts.length === 0
                "
                :error-message="
                  [
                    !selectedDestinVolume &&
                      'Please select a destination volume',
                    destinationVolumesOpts.length === 0 &&
                      `Please make sure destination volume has correctly been registered in ${brandShort}`,
                  ].find(Boolean)
                "
              >
                <template #option="{ option }">
                  <a
                    v-if="option.is_primary_volume"
                    v-tooltip="'This is the primary volume'"
                    class="icon is-small mr-2"
                    style="color: gold"
                  >
                    <i class="fa-solid fa-star fa-sm" />
                  </a>
                  {{ option.name }}
                  <span
                    class="tag is-size-7 ml-2"
                    :class="!option.isS3 && 'is-info'"
                  >
                    {{ option.isS3 ? 'S3' : option.storagebackend }}
                  </span>
                </template>
              </SelectContainer>
            </div>
            <div class="field">
              <label class="label is-size-7 mx-1 mb-1">
                Destination Directory
              </label>
              <span class="is-flex is-align-items-center">
                <code v-if="selectedDestinVolume" class="has-text-primary">
                  {{ selectedDestinVolume?.base_path }}
                </code>
                <div class="control is-flex-grow-1">
                  <input
                    v-model.trim="copyDirectory"
                    class="input is-small"
                    :class="{
                      'is-danger':
                        selectedDestinVolume && !copyDirectory.trim(),
                    }"
                    type="text"
                    placeholder="<directory>"
                    :disabled="!selectedDestinVolume"
                  />
                </div>
              </span>
            </div>
            <div class="field">
              <label class="label is-size-7 mx-1 mb-1">Copy Strategy</label>
              <div class="select is-small is-fullwidth">
                <select
                  v-model="copyStrategy"
                  class="control select"
                  :placeholder="'Select a Copy Strategy'"
                >
                  <option disabled default value="">Select option</option>
                  <option
                    v-for="{ value, label } in Object.values(copyStrategies)"
                    :key="`copy-opt-${value}`"
                    :value="value"
                  >
                    {{ label }}
                  </option>
                </select>
              </div>
              <p class="help mb-2">
                <span class="icon">
                  <i class="fa fa-info-circle has-text-info" />
                </span>
                <span>
                  {{ copyStrategies[copyStrategy].message || '' }}
                </span>
              </p>
            </div>
            <div class="field">
              <label class="label is-size-7 mx-1 mb-1">
                How to handle exisiting files?
              </label>
              <div class="select is-small is-fullwidth">
                <select v-model="handleExisting" class="control select">
                  <template
                    v-for="{
                      value,
                      label,
                      isDefault,
                      // message,
                    } of handleExistingOpts"
                    :key="`copy-opt-${value}`"
                  >
                    <option :value="value" :default="isDefault">
                      {{ label }}
                    </option>
                  </template>
                </select>
              </div>
              <p class="help mb-2">
                <span class="icon">
                  <i class="fa fa-info-circle has-text-info" />
                </span>
                <span>
                  {{ handleExistingStrategies[handleExisting].message || '' }}
                </span>
              </p>
            </div>
            <div v-if="isCopyFormValid" class="sumup my-5">
              <p class="mb-2">
                <span class="icon mr-1">
                  <i class="fa fa-info-circle has-text-info" />
                </span>
                The data will be copied to
                <code class="has-text-primary">
                  {{ [destinVolumeBasePath, copyDirectory].join('/') }}
                </code>
              </p>
              <div
                v-if="isCopyFormValid"
                class="tree-view is-flex is-flex-align-center is-flex-jutify-center"
              >
                <div
                  v-if="errors['no-dfc-destination-paths']"
                  class="notification is-danger"
                >
                  <span class="icon">
                    <i class="fa fa-exclamation-triangle" />
                  </span>
                  <span class="text">
                    {{ errors['no-dfc-destination-paths'] }}
                  </span>
                </div>
                <file-tree
                  v-else
                  :key="`dfcsDestinations-${dfcsDestinations.length}`"
                  ref="fileTree"
                  :static-path-list="dfcsDestinations"
                  :root-node="{
                    name: destinVolumeBasePath,
                    relativePath: destinVolumeBasePath,
                    is_dir: true,
                  }"
                  :is-loading="isLoadingDestinationPaths"
                  tree-class="py-2 px-4"
                  :tree-style="treeStyle"
                >
                  <template #toolbar>
                    <div style="">
                      <p class="mb-2" style="background-color: white">
                        <span class="icon mr-1">
                          <i class="fa fa-info-circle has-text-info" />
                        </span>
                        Destination tree
                        <span
                          class="tag is-small is-primary ml-2 mr-2"
                          style="height: 1rem"
                        >
                          {{ dfcsDestinations.length }} files
                        </span>
                        <span
                          v-if="hasErrors"
                          class="tag is-small is-danger ml-2"
                          style="height: 1rem"
                        >
                          {{ errorCount }}
                          {{ pluralize('error', errorCount) }}
                        </span>
                      </p>
                      <div
                        class="notification is-info is-light is-size-7 m-0"
                        style="border-radius: 5px 5px 0 0"
                      >
                        <span class="icon p-0">
                          <i class="fa fa-info" />
                        </span>
                        <span class="text has-text-left">
                          <p>
                            The destination tree is a representation of how the
                            <b>selected data</b>
                            will be organised on the destination volume, based
                            e.g. on the copy strategy.
                          </p>
                          <p>
                            <b>
                              It does not show the data that would already be
                              located on the destination volume (and potentially
                              overwritten).
                            </b>
                          </p>
                        </span>
                      </div>
                    </div>
                  </template>
                  <template #text="{ model }">
                    <div
                      class="is-flex is-align-items-center is-flex-wrap-wrap"
                    >
                      <span
                        :class="{
                          'has-text-weight-semibold': !model.is_dir,
                          'pb-1': model.is_dir,
                        }"
                      >
                        {{ model[model.treeNodeSpec.labelProperty] }}
                        <span v-if="model.is_dir">/</span>
                      </span>
                      <p
                        v-if="
                          hasErrors &&
                          dfcErrors[model.uri] &&
                          dfcErrors[model.uri].length
                        "
                        class="ml-2 is-size-7 has-text-weight-semibold"
                      >
                        <template
                          v-for="(
                            { type, message, suggested_actions }, index
                          ) of dfcErrors[model.uri]"
                          :key="`error-${type}-${model.uri}${index}`"
                        >
                          <span
                            v-tooltip="{
                              html: true,
                              content: `<ul>
                              <li>${suggested_actions
                                .map((a) => SUGGEDTED_ERROR_ACTIONS[a])
                                .join('</li><li>')}</li>
                              </ul>`,
                            }"
                            class="has-text-danger"
                          >
                            <span class="icon has-text-danger">
                              <i class="fa fa-exclamation-triangle" />
                            </span>
                            {{ message }}
                            <!-- {{
                              
                            }} -->
                          </span>
                        </template>
                      </p>
                    </div>
                  </template>
                </file-tree>
              </div>
            </div>
          </fieldset>
        </DefaultPanel>
      </template>
    </template>
    <template #footer>
      <div style="width: 100%; text-align: right">
        <a
          class="button is-outlined is-small"
          :class="{ 'is-disabled': false /*isRestoring*/ }"
          @click="closeModal()"
        >
          Cancel
        </a>
        <BaseButton
          class="has-text-weight-bold ml-auto"
          variant="primary"
          size="small"
          :disabled="
            transferableDatasetsCount === 0 ||
            !isCopyFormValid ||
            isLoadingDestinationPaths ||
            isCopyingDatasets ||
            dfcsDestinations.length === 0
          "
          :loading="isCopyingDatasets"
          :icon="['fa-solid', 'fa-cloud-upload-alt']"
          label="Copy to S3"
          @click="copyDatasets"
        />
      </div>
    </template>
  </ModalCard>
</template>

<script>
import { ref, computed, watch, watchEffect, nextTick } from 'vue'
import { debounce } from 'lodash-es'
import pluralize from 'pluralize'
import { get, post } from '@/api'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { useStore } from '@/store'
import { loadDatasets } from '@/utils/api/dataManagement'

import DefaultPanel from '@/components/DefaultPanel.vue'
import FileTree from '@/components/FileTree.vue'
import SelectContainer from '@/components/SelectContainer.vue'
import StatusWidget from '@/components/StatusWidget.vue'
import { brandShort } from '@/../app.config'

const COPY_STRATEGIES = {
  REPLICATE: {
    order: 0,
    default: true,
    value: 'same-structure',
    label: 'Keep source directory structure',
    message:
      'The source directory structure will be replicated inside the destination directory.',
  },
  UNIQUE: {
    order: 0,
    value: 'unique',
    label: 'Flatten source directory structure (and generate unique names)',
    message:
      'Unique names will be generated for each file and all files will be placed inside the destination directory.',
  },
}
const HANDLE_EXISTING = {
  // handle_existing ["error", "overwrite", "overwrite_if_newer", "skip"]:
  SKIP: {
    order: 0,
    default: true,
    value: 'skip',
    label: 'Skip existing files',
    message: 'Files that already exist at destination will not be transfered.',
  },
  ERROR: {
    order: 1,
    value: 'error',
    label: 'Fail if some files exist',
    message: 'The copy operation will fail if some files already exist.',
  },
  OVERWRITE: {
    order: 2,
    value: 'overwrite',
    label: 'Overwrite',
    message: 'Any existing file at destination will be overwritten',
  },
  OVERWRITE_IF_NEWER: {
    order: 3,
    value: 'overwrite_if_newer',
    label: 'Overwrite (when file is newer)',
    message:
      'Files at the destination will only be replaced if the source file is newer than the existing one.',
  },
}

const SUGGEDTED_ERROR_ACTIONS = {
  change_destination_path:
    'Change destination path, for example by chosing a different destination directory.',
  use_handle_existing_overwrite:
    'Use "Overwrite" strategy to overwrite existing files.',
  use_handle_existing_skip: ' Use "Skip" strategy to skip existing files.',
}

export default {
  name: 'DatasetCopyModal',
  emits: ['update:is-opened'],
  components: {
    DefaultPanel,
    FileTree,
    SelectContainer,
    StatusWidget,
    DynamicScroller,
    DynamicScrollerItem,
  },
  props: {
    isOpened: { type: Boolean, required: true },
    records: { type: Array },
    folderName: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const store = useStore()
    const storageVolumes = ref([])

    const isLoadingStorageVolumes = ref(false)
    const errors = ref({
      'storage-volumes': undefined,
      'no-dfc-destination-paths': undefined,
    })
    const autoSelectPrimaryVolume = true
    const canChangeSourceVolume = false
    const showSourceInfo = ref(false)
    const selectedSourceVolume = ref(null)
    const autoselectSingleDestinVolume = true
    const selectedDestinVolume = ref(null)
    const destinVolumeBasePath = computed(() => {
      let dstPath = selectedDestinVolume.value?.base_path
      // remove potential trailing slashes
      return dstPath?.replace(/\/+$/, '')
    })
    const copyDirectory = ref(props.folderName)
    const copyStrategies = Object.values(COPY_STRATEGIES).reduce(
      (strategies, s) => ({ ...strategies, [s.value]: s }),
      {},
    )
    const copyStrategy = ref(Object.values(COPY_STRATEGIES)[0].value)

    const closeModal = () => emit('update:is-opened', false)

    async function fetchStorageVolumes() {
      let sv = []
      try {
        errors.value['storage-volumes'] = null
        isLoadingStorageVolumes.value = true
        const response = await get(
          'data_management/storagevolumes/?response_format=flat',
        )
        if (response.status === 200) sv = response.data.results
      } catch (e) {
        errors.value['storage-volumes'] = 'Error fetching storage volumes'
      } finally {
        isLoadingStorageVolumes.value = false
      }
      return sv
    }

    // Datasets are either passed from props.records or fetched from the API
    // with the help of queryParameters prop (e.g. { assay_id: id })
    const getMetaContext = (record) => {
      const { app_name, model_name } = record
      const appName = app_name.value?.toLowerCase()
      const modelName = model_name.value?.toLowerCase()
      return { appName, modelName }
    }
    // const isDatasetModel = ({ appName, modelName }) => {
    //   return appName === 'data_management' && modelName === 'dataset'
    // }
    const currentContext = computed(() => {
      const oneRecord = props.records[0]
      return getMetaContext(oneRecord)
    })
    const isDatasetContext = computed(() => {
      const { appName, modelName } = currentContext.value
      return appName === 'data_management' && modelName === 'dataset'
    })

    const datasetsData = ref([])
    const isLoadingDatasets = ref(false)
    watchEffect(async () => {
      if (!props.isOpened) return
      if (isDatasetContext.value) {
        datasetsData.value = props.records
      } else {
        try {
          isLoadingDatasets.value = true
          const { modelName } = getMetaContext(props.records[0])
          const queryParameters = {
            deleted: 'false',
            page_size: 'max',
            [`${modelName}_ids`]: props.records.map((r) => r.id.value),
          }
          datasetsData.value = await loadDatasets({ queryParameters })
        } catch (err) {
          console.error('Failed determining copy to S3 modal context')
          datasetsData.value = []
        } finally {
          isLoadingDatasets.value = false
        }
      }
    })

    const datasets = computed(() =>
      datasetsData.value.reduce((datasets, d) => {
        const status = d.status.value
        const isOnline = status.value === 'ONLINE'
        const perms = d.permissions.value.allowed
        const isPublic = d.public.value
        const hasPerms = perms.includes('view') || isPublic
        const canCopy = isOnline && hasPerms
        const name = d.name.value.name
        // const parentName =
        const id = d.id.value
        return {
          [id]: {
            id,
            name,
            status,
            isOnline,
            hasPerms,
            canCopy,
          },
          ...datasets,
        }
      }, {}),
    )
    const datasetsCount = computed(() => Object.keys(datasets.value).length)
    const transferableDatasets = computed(() =>
      Object.values(datasets.value).filter((d) => d.isOnline && d.hasPerms),
    )
    const transferableDatasetsCount = computed(
      () => transferableDatasets.value.length,
    )
    const nonTransferableDatasets = computed(() =>
      Object.values(datasets.value).filter((d) => !d.isOnline || !d.hasPerms),
    )
    const noPermsCount = ref(0)
    const offlineCount = ref(0)
    watch(
      nonTransferableDatasets,
      (d) => {
        noPermsCount.value = d.filter((d) => !d.hasPerms).length
        offlineCount.value = d.filter((d) => !d.isOnline).length
      },
      { immediate: true },
    )

    const isCopyFormValid = computed(() => {
      return (
        selectedDestinVolume.value &&
        copyDirectory.value.trim() &&
        copyStrategy.value
      )
    })

    const showExisting = ref(false)
    const resolveDestPayload = computed(() => {
      // {
      //   strategy: 'same-structure|unique',
      //   destination_storagevolume: 'uuid',
      //   destination_root_dir: 'path',
      //   duplicate_strategy: 'skip|ignore',
      //   datasets: [
      //     {
      //       id: 'id',
      //       datafilecopies: [ { id }],
      //     },
      //   ],
      // }
      return {
        strategy: copyStrategy.value,
        destination_storagevolume: selectedDestinVolume.value?.id,
        destination_root_dir: copyDirectory.value,
        autoselect_datafilecopies: true,
        duplicate_strategy: showExisting.value ? 'ignore' : 'skip',
        datasets: Object.values(transferableDatasets.value).map((d) => ({
          id: d.id,
        })),
      }
    })

    const dfcsDestinations = ref([])
    const datasetsDfcPayload = ref([])
    const isLoadingDestinationPaths = ref(false)
    let abortController
    async function getDfcDestinationsPaths() {
      try {
        if (abortController) abortController.abort()
        abortController = new AbortController()
        errors.value['no-dfc-destination-paths'] = undefined
        isLoadingDestinationPaths.value = true
        const response = await post(
          'data_management/datasets/resolve_destination_paths/',
          { data: resolveDestPayload.value, signal: abortController.signal },
        )
        if (response.status === 200) {
          errors.value['no-dfc-destination-paths'] = null
          datasetsDfcPayload.value = response.data
          const datasetsDfcData = response.data.datasets
          dfcsDestinations.value = datasetsDfcData
            .map((d) => d.datafilecopies.map((dfc) => dfc.destination_path))
            .flat()
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          const message =
            err?.response?.data?.message ||
            'Could not recover datafile copies destination path'
          errors.value['no-dfc-destination-paths'] = new Error(message)
        }
      } finally {
        isLoadingDestinationPaths.value = false
      }
    }
    // Create a debounced version of the function
    const debouncedGetDfcDestinationsPaths = debounce(
      getDfcDestinationsPaths,
      1000,
      { leading: true },
    )

    const treeStyle = computed(() => {
      const h = prevHeight.value <= 400 ? prevHeight.value : 400
      const baseStyle = {
        width: '100%',
        'max-height': `${h}px`,
        border: '1px solid var(--color-border-default)',
        overflow: 'auto',
      }
      const loadingStyle = {
        'min-height': `${h}px`,
      }
      return {
        ...baseStyle,
        ...((isLoadingDestinationPaths.value && loadingStyle) || {}),
      }
    })

    // Watch for changes and call the debounced function
    watchEffect(() => {
      const isValid =
        selectedDestinVolume.value &&
        copyDirectory.value.trim() &&
        copyStrategy.value
      if (isValid) {
        debouncedGetDfcDestinationsPaths.cancel()
        debouncedGetDfcDestinationsPaths()
      }
    })

    const fileTree = ref(null)
    const prevHeight = ref(null)
    watch(isLoadingDestinationPaths, async (isLoading) => {
      if (!isLoading) {
        await nextTick()
        prevHeight.value = fileTree.value?.$el?.clientHeight
      }
    })

    const handleExistingStrategies = Object.values(HANDLE_EXISTING).reduce(
      (strategies, s) => ({
        ...strategies,
        [s.value]: { ...s, isDefault: s.default },
      }),
      {},
    )
    const handleExistingOpts = Object.values(handleExistingStrategies).sort(
      (a, b) => a.order - b.order,
    )
    const handleExisting = ref(handleExistingOpts.find((s) => s.default)?.value)

    const isCopyingDatasets = ref(false)
    const dfcErrors = ref({})
    const errorCount = computed(() => Object.keys(dfcErrors.value).length || 0)
    const hasErrors = computed(() => errorCount.value > 0)
    const copyDatasetPayload = computed(() => {
      return {
        ...datasetsDfcPayload.value,
        handle_existing: handleExisting.value,
      }
    })
    async function copyDatasets() {
      try {
        dfcErrors.value = {}
        isCopyingDatasets.value = true
        const response = await post('data_management/datasets/copy/', {
          data: copyDatasetPayload.value,
        })
        if (response.status >= 200 && response.status < 300) {
          const { message } = response.data
          store.commit('notify', { type: 'success', message })
          closeModal()
        }
      } catch (error) {
        const errorData = error.response.data
        const errorSummary = errorData?.error_summary || {}
        const errorCount = errorSummary.total_errors
        const errorTypes = (errorSummary.error_types || []).join(', ')
        let summaryMessage = `Failed with ${errorCount} errors`
        if (errorTypes) summaryMessage += ` (${errorTypes})`

        let message = errorCount
          ? summaryMessage
          : error?.response?.data?.message || 'Could not copy datasets'
        errorData.datasets?.forEach(({ datafilecopies }) => {
          datafilecopies.forEach(
            ({
              destination_path: destinationPath,
              validation_errors: errors,
            }) => {
              if (error) dfcErrors.value[destinationPath] = errors
            },
          )
        })
        if (Object.keys(dfcErrors.value).length > 0) {
          message += '<br/>Check destination tree for detailed information'
        }
        store.commit('notify', { type: 'error', message })
      } finally {
        isCopyingDatasets.value = false
      }
    }

    const getFirstPrimaryVolume = (volumes) =>
      volumes?.find((v) => v.is_primary_volume) || null

    async function init() {
      const sv = await fetchStorageVolumes()
      const s3sv = sv
        .filter((v) => v.storagebackend.toLowerCase() === 's3storagebackend')
        .map((sv) => ({ ...sv, isS3: true }))
      storageVolumes.value = s3sv
      if (autoSelectPrimaryVolume) {
        const primaryVolume = getFirstPrimaryVolume(s3sv)
        selectedSourceVolume.value = primaryVolume
      }
    }

    const destinationVolumesOpts = ref([])
    watchEffect(() => {
      const selectedVolId = selectedSourceVolume.value?.id
      destinationVolumesOpts.value = storageVolumes.value.filter(
        (v) => v.id !== selectedVolId,
      )
      if (autoselectSingleDestinVolume && storageVolumes.value.length === 1) {
        selectedDestinVolume.value = storageVolumes.value[0]
      }
    })

    watch(
      () => props.isOpened,
      async (isOpened) => isOpened && init(),
      { immediate: true },
    )

    return {
      SUGGEDTED_ERROR_ACTIONS,
      brandShort,
      isLoadingDatasets,
      isLoadingStorageVolumes,
      isLoading: computed(
        () => isLoadingDatasets.value || isLoadingStorageVolumes.value,
      ),
      errors,
      datasetsCount,
      storageVolumes,
      selectedSourceVolume,
      showSourceInfo,
      autoSelectPrimaryVolume,
      canChangeSourceVolume,
      datasets,
      transferableDatasets,
      transferableDatasetsCount,
      nonTransferableDatasets,
      noPermsCount,
      offlineCount,
      destinationVolumesOpts,
      selectedDestinVolume,
      destinVolumeBasePath,
      copyDirectory,
      copyStrategies,
      copyStrategy,
      isCopyFormValid,
      closeModal,
      //
      datasetsData,
      showExisting,
      isLoadingDestinationPaths,
      dfcsDestinations,
      fileTree,
      treeStyle,
      prevHeight,
      //
      resolveDestPayload,
      handleExistingStrategies,
      handleExistingOpts,
      handleExisting,
      datasetsDfcPayload,
      copyDatasets,
      copyDatasetPayload,
      isCopyingDatasets,
      dfcErrors,
      errorCount,
      hasErrors,
    }
  },
  methods: {
    pluralize,
  },
}
</script>

<style lang="scss" scoped>
.scroller {
  display: block;
  min-height: 50px;
  height: auto;
  max-height: 33vh;
  resize: vertical;
  padding: 0;

  :deep(.vue-recycle-scroller__item-view) {
    border-bottom: 1px solid var(--color-border-default);
    background-color: var(--color-background-gray-light);
    .even {
      background-color: var(--white);
    }
  }
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.tree-view {
  position: relative;
}
</style>
